import { useState, useEffect } from 'react';
import { useConnections, useApps, useProfiles, useAuth } from '../stores/appStore';
import { mockUser, mockProfiles } from '../lib/mockData';
import { AppGrid } from '../components/discovery/AppGrid';
import { SearchBar } from '../components/common/SearchBar';
import { 
  ChartBarIcon, 
  RectangleStackIcon, 
  CubeIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

const categories = [
  { id: 'all', name: 'All Apps', count: 0 },
  { id: 'communication', name: 'Communication', count: 0 },
  { id: 'productivity', name: 'Productivity', count: 0 },
  { id: 'crm', name: 'CRM', count: 0 },
  { id: 'finance', name: 'Finance', count: 0 },
  { id: 'marketing', name: 'Marketing', count: 0 },
  { id: 'development', name: 'Development', count: 0 },
];

export function Dashboard() {
  const { connections, fetchConnections } = useConnections();
  const { apps, fetchApps } = useApps();
  const { profiles, setProfiles } = useProfiles();
  const { user, setUser, setAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    // Initialize with mock data for development
    if (!user) {
      setUser(mockUser);
      setAuthenticated(true);
      setProfiles(mockProfiles);
    }
    
    fetchApps();
    fetchConnections();
  }, [user, setUser, setAuthenticated, setProfiles, fetchApps, fetchConnections]);

  const activeConnections = connections.filter(conn => conn.status === 'active');
  const connectedApps = new Set(activeConnections.map(conn => conn.appId));

  const stats = [
    {
      name: 'Active Connections',
      value: activeConnections.length,
      icon: RectangleStackIcon,
      change: '+4.75%',
      changeType: 'positive',
    },
    {
      name: 'Connected Apps',
      value: connectedApps.size,
      icon: CubeIcon,
      change: '+54.02%',
      changeType: 'positive',
    },
    {
      name: 'Total Profiles',
      value: profiles.length,
      icon: UsersIcon,
      change: '-1.39%',
      changeType: 'negative',
    },
    {
      name: 'Available Apps',
      value: apps.length,
      icon: ChartBarIcon,
      change: '+10.18%',
      changeType: 'positive',
    },
  ];

  return (
    <div className="space-y-6 sm:space-y-8" data-tour="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          Dashboard
        </h1>
        <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          Manage your Pipedream MCP connections and discover new apps
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
        {stats.map((item) => (
          <div
            key={item.name}
            className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div className="p-3 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <item.icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" aria-hidden="true" />
                </div>
                <div className="ml-3 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      {item.name}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-white">
                        {item.value}
                      </div>
                      <div className={`ml-2 flex items-baseline text-xs sm:text-sm font-semibold ${
                        item.changeType === 'positive' 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {item.change}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-4">
            Discover Apps
          </h3>
          
          {/* Search and Filters */}
          <div className="space-y-4 mb-6">
            <SearchBar
              placeholder="Search 2,500+ apps..."
              onSearch={setSearchQuery}
              className="max-w-full lg:max-w-lg"
              data-tour="app-search"
            />
            
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* App Grid */}
          <div data-tour="app-grid">
            <AppGrid 
              searchQuery={searchQuery}
              category={selectedCategory}
              limit={12}
            />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-4">
            Recent Activity
          </h3>
          
          <div className="space-y-2 sm:space-y-3">
            {activeConnections.slice(0, 5).map((connection) => {
              const app = apps.find(a => a.id === connection.appId);
              return (
                <div key={connection.id} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-[10px] sm:text-xs">
                      {app?.name.charAt(0) || 'A'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                      Connected to {app?.name || 'Unknown App'}
                      {connection.accountName && (
                        <span className="text-gray-500 dark:text-gray-400">
                          {' '}({connection.accountName})
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                      {new Date(connection.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
                      Active
                    </span>
                  </div>
                </div>
              );
            })}
            
            {activeConnections.length === 0 && (
              <div className="text-center py-6 sm:py-8">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No connections yet. Start by connecting to your first app!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}